import create from 'zustand';
import useUIConfigStore from 'stores/useUIConfigStore';
import { addBreadcrumb, captureException } from '@sentry/nextjs';
import auth from 'utils/auth';
import { FeatureFlagKeys } from '../constants/flagKeys';
import { evaluateBoolean, evaluateVariant } from '../endpoints';

interface State {
  userId?: string;
}

interface Actions {
  setUserId: (userId: number | string) => void;
  loadBooleanEvaluation(
    flagKey: FeatureFlagKeys,
    namespaceKey?: string,
    enableGuest?: boolean,
  ): Promise<boolean>;
  loadVariantEvaluation(flagKey: FeatureFlagKeys, namespaceKey: string);
}

const useFliptStore = create<Actions & State>((set, get) => ({
  setUserId(userId) {
    set({ userId: userId ? String(userId) : undefined });
  },

  async loadBooleanEvaluation(flagKey, namespaceKey, enableGuest) {
    try {
      const userId =
        get().userId || auth.getUserAccess()?.id?.toString() || 'guest';

      if (userId === 'guest' && !enableGuest) {
        // Return a rejected promise if user id is not found
        // to prevent the function from continuing
        addBreadcrumb({
          message: 'Flipt:loadBooleanEvaluation: missing userId (guest user)',
          category: 'feature-flag',
          level: 'info',
          data: {
            flagKey,
            namespaceKey,
          },
        });

        return false;
      }
      const { getUIValue, setUIValue } = useUIConfigStore.getState();

      const featureFlags = getUIValue('featureFlags');
      // immidiate return if feature flag value is already exists
      if (featureFlags && featureFlags[flagKey]) {
        return featureFlags[flagKey].isEnabled;
      }
      const namespace = namespaceKey ?? 'transaction';
      const response = await evaluateBoolean(userId, flagKey, namespace);
      setUIValue('featureFlags', (draft) => {
        draft[flagKey] = {
          isEnabled: response.data.enabled,
        };
      });

      return response.data.enabled;
    } catch (error) {
      captureException(error, {
        tags: {
          action: 'Flipt:loadBooleanEvaluation',
          flagKey,
          namespaceKey,
        },
      });

      // Return false as fallback behavior
      return false;
    }
  },

  async loadVariantEvaluation(flagKey, namespaceKey) {
    try {
      const userId =
        get().userId || auth.getUserAccess()?.id?.toString() || 'guest';

      if (userId === 'guest') {
        // Return a rejected promise if user id is not found
        // to prevent the function from continuing
        addBreadcrumb({
          message: 'Flipt:loadVariantEvaluation: missing userId (guest user)',
          category: 'feature-flag',
          level: 'info',
          data: {
            flagKey,
            namespaceKey,
          },
        });
        return false;
      }

      const { setUIValue } = useUIConfigStore.getState();
      // immidiate return if feature flag value is already exists
      const response = await evaluateVariant(userId, flagKey, namespaceKey);
      setUIValue('featureFlags', (draft) => {
        draft[flagKey] = {
          variance: response.data,
        };
      });
      return response.data;
    } catch (error) {
      Promise.reject(error);
      // default behavior
      return false;
    }
  },
}));

export default useFliptStore;
